import TableBody from '@mui/material/TableBody';
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { IoFilter } from 'react-icons/io5';
import { TbCreditCard } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';
import { AdminTitle } from '../../components/AdminTitle';
import { Loading } from '../../components/Loading';
import ReactSelect from '../../components/ReactSelect';
import { StyledButton } from '../../components/StyledButton';
import { StyledInput } from '../../components/StyledInput';

import theme from '../../styles/themes/default';
import {
  ExportContainer,
  FiltersContainer,
  InfoCardsWrapper,
  MandatoryDebtsContainer,
  VehicleContainer,
} from './style';
import { PiArrowClockwise, PiArrowLineUpRight, PiMoney } from 'react-icons/pi';
import { LuAlignEndHorizontal } from 'react-icons/lu';
import InfoCard from './components/InfoCard';
import { IMandatoryDebt, useDebtsInfo } from '../../hooks/useDebts';
import DebtsDataTable, { HeadCell } from './components/DebtsDataTable';
import ConfigureTableModal, { IItem } from './components/ConfigureTableModal';
import ConfiguredTableRow from './components/ConfiguredTableRow';
import AdvancedFiltersModal, { IHandleApplyFiltersProps as IHandleApplyAdvancedFiltersProps } from './components/AdvancedFiltersModal';
import { useBatchPayment } from '../../context/BatchPaymentContext';
import Swal from 'sweetalert2';
import GenericModal from '../../components/GenericModal';
import { HiClock } from 'react-icons/hi';
import { IoIosArrowDroprightCircle } from 'react-icons/io';
import { createExportedFile } from '../../hooks/useExports';
import { LoadingConsult } from '../../components/LoadingConsult';
import { AiFillQuestionCircle } from 'react-icons/ai';
import { FaCircleCheck } from 'react-icons/fa6';
import { ConsultsPackageInProgress } from '../UserVehicles/style';
import ProgressBar from '@ramonak/react-progress-bar';
import { useVehicle } from '../../context/VehiclesContext';
import formatDateToISO from '../../utils/formatDate';
import { UF } from '../../utils/enums';
import { getEnumKeyByValue } from '../../utils/ufsNamed';
import { BatchProgressStatusBar } from '../../components/BatchProgressStatusBar';

const headersInitialState = [
  { key: 'vehiclePlate', label: 'Placa', id: 'item-0' },
  { key: 'vehicleUF', label: 'UF', id: 'item-1', className: "uf-column" },
  { key: 'type', label: 'Débito', id: 'item-2' },
  { key: 'description', label: 'Descrição', id: 'item-3', className: "description-column" },
  { key: 'price', label: 'Valor', id: 'item-4' },
  { key: 'totalMandatory', label: 'Débitos Obrigatórios', id: 'item-5', className: "mandatory-column" },
  { key: 'total', label: 'Total de Débitos', id: 'item-6' },
  { key: 'driverName', label: 'Condutor', id: 'item-7', className: "driver-column" },
  { key: 'isExpired', label: 'Status', id: 'item-8' },
  { key: 'consultDate', label: 'Atualizado em', id: 'item-9', className: "updated-column" },
  { key: 'expirationDate', label: 'Vencimento', id: 'item-10' },
];

export const statusOptions = [
  // { value: 'PENDING', label: 'Pag. Pendente' },
  { value: 'EXPIRING', label: 'A Vencer' },
  { value: 'EXPIRED', label: 'Vencido' },
];

export const debtTypeOptions = [
  { value: 'ipva', label: 'IPVA' },
  { value: 'licenciamento', label: 'Licenciamento' },
  { value: 'multas', label: 'Multa' },
  { value: 'outros', label: 'Outros' },
];

const DebtsListing: FC = () => {
  const [orderByDirection, setOrder] = useState<'desc' | 'asc'>('desc');
  const [orderBy, setOrderBy] = useState('consultDate');
  const [itemsPerPage, _] = useState(100);
  const [plateToShow, setPlateToShow] = useState<string | undefined>('');
  const [exportFilename, setExportFilename] = useState<string>('');
  const [totalMultas, setTotalMultas] = useState<number>(0);
  const [totalIPVA, setTotalIPVA] = useState<number>(0);
  const [totalLicenciamento, setTotalLicenciamento] = useState<number>(0);
  const [totalOutros, setTotalOutros] = useState<number>(0);
  const [mandatoryDebts, setMandatoryDebts] = useState<IMandatoryDebt[]>([]);

  // advancedFilters
  const [plate, setPlate] = useState<string | undefined>();
  const [driver, setDriver] = useState<string | undefined>();
  const [initialDate, setInitialDate] = useState<Date | string | undefined>();
  const [endDate, setEndDate] = useState<Date | string | undefined>();
  const [parcelaIpva, setParcelaIpva] = useState<string | undefined>();
  const [endPlate, setEndPlate] = useState<string | undefined>();
  const [uf, setUf] = useState<
    { value: string; label: string } | undefined
  >();
  const [isUniqueInstallments, setIsUniqueInstallments] = useState<boolean | undefined>();

  const [isLoading, setIsLoading] = useState(false);

  const [openConfigureTableModal, setOpenConfigureTableModal] =
    useState<boolean>(false);
  const [openGlobalFiltersModal, setOpenGlobalFiltersModal] =
    useState<boolean>(false);
  const [openExportModal, setOpenExportModal] = useState<boolean>(false);
  const [openGeneratingExportModal, setOpenGeneratingExportModal] =
    useState<boolean>(false);
  const [openMandatoryDebtsModal, setOpenMandatoryDebtsModal] =
    useState<boolean>(false);

  const [tableColumnsConfig, setTableColumnsConfig] = useState<
    { id: string; content?: string; show: boolean }[]
  >([]);

  const [headers, setHeaders] =
    useState<({ key: string; label: string } | undefined)[]>(
      headersInitialState,
    );

  const [debtType, setDebtType] = useState<
    { value: string; label: string } | undefined
  >(undefined);
  const [debtStatus, setDebtStatus] = useState<
    { value: string; label: string } | undefined
  >(undefined);

  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const [isChecked, setIsChecked] = useState<{ [key: string]: boolean }>({});
  const [isAllChecked, setIsAllChecked] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showIsLoadingFleet, setShowIsLoadingFleet] = useState(false);
  const [showFinishLoadingFleet, setShowFinishLoadingFleet] = useState(false);

  const {
    selectedDebts,
    setSelectedDebts,
    handleConsultClean,
  } = useBatchPayment();

  const {
    vehiclesTotal,
    handleConsultsPackageStatus,
    consultsCompletedStatus,
    handlePostConsultsPackageByUserSub,
    batchDebtsConsultsProgress,
    consultsPackageStatus,
    consultsPackageModal,
    setConsultsPackageModal,
    arrayActionsConfirmModal,
    loadingConsult,
    packAndPostConsultsByDebts,
    currentDebtsConsultsBatch,
    successfulAndFailedDebtConsults
  } = useVehicle();

  const handleValue = (value: string | undefined) => {
    if (value && value.length > 0) return value;
    return undefined;
  };

  const {
    data: dataList,
    isLoading: isLoadingDebts,
    error,
    refetch,
  } = useDebtsInfo({
    duplicated: false,
    resultsPerPage: itemsPerPage,
    page,
    type: debtType?.value,
    vehicleUF: uf?.value,
    installments: parcelaIpva,
    plate,
    dueDateStart: initialDate?.toString(),
    dueDateEnd: endDate?.toString(),
    isExpirationDate: debtStatus?.value
      ? debtStatus.value !== 'EXPIRING'
      : undefined,
    isUniqueInstallments,
    orderByDirection,
    driverName: driver,
    finalNumberPlate: endPlate,
    orderBy,
  });

  useEffect(() => {
    if (!consultsPackageModal) {
      setShowConfirmModal(false);
      setShowIsLoadingFleet(false);
      setShowFinishLoadingFleet(false);
    } else {
      switch (consultsPackageModal.type) {
        case 'confirm':
          if (consultsPackageModal.success) {
            setShowConfirmModal(true);
          }
          break;

        case 'loading':
          if (consultsPackageModal.success) {
            setShowIsLoadingFleet(true);
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Falha ao enviar pacote de consultas!',
              html:
                (consultsPackageModal.message as string) ||
                `Houve um erro inesperado ao enviar pacote de consultas.`,
              confirmButtonColor: theme.colors.orange.primary,
            }).then((onClose) => setConsultsPackageModal(undefined));
          }
          break;

        case 'finished':
          if (consultsPackageModal.success) {
            setShowFinishLoadingFleet(true);
          }
          break;

        default:
          setShowConfirmModal(false);
          setShowIsLoadingFleet(false);
          setShowFinishLoadingFleet(false);
          break;
      }
    }
  }, [consultsPackageModal]);

  useEffect(() => {
    console.log("This is DataList: ", dataList)
    const filteredDebts =
    dataList?.results?.filter(
      (debt) => debt.debtsCode in isChecked && isChecked[debt.debtsCode],
    ) ?? [];
    console.log("This are selected filtered Debts: ", filteredDebts )
    setSelectedDebts(filteredDebts);
  }, [isChecked, dataList]);

  useEffect(() => {
    itemsPerPage > 10 && setPage(1);
    Number(dataList?.total) < itemsPerPage && setPage(1);
  }, [dataList?.total, itemsPerPage]);

  const totalDebtsData = useMemo(() => {
    const totalSelected =
      totalLicenciamento + totalIPVA + totalMultas + totalOutros;

    return [
      {
        titleCard: 'Licenciamento',
        totalValue: `R$ ${(totalLicenciamento || 0)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      },
      {
        titleCard: 'IPVA',
        totalValue: `R$ ${(totalIPVA || 0)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      },
      {
        titleCard: 'Multas',
        totalValue: `R$ ${(totalMultas || 0)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      },
      {
        titleCard: 'Outros',
        totalValue: `R$ ${(totalOutros || 0)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      },
      {
        titleCard: 'Total',
        totalValue: `R$ ${(totalSelected || 0)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      },
    ];
  }, [totalIPVA, totalLicenciamento, totalMultas, totalOutros]);


  useEffect(() => {
    let selectedOrAllDebts = dataList?.results.filter(
      (debt) =>
        debt?.debtsCode?.toString() in isChecked &&
        isChecked[debt?.debtsCode?.toString()],
    ) ?? [];

    if (!(selectedOrAllDebts?.length > 0)) {
      selectedOrAllDebts = dataList?.results ?? []
    }
    
    let selectedDebtsAndMandatoryByType: IMandatoryDebt[] = [];
    let allSelectedCodes: number[] = [];

    selectedOrAllDebts.forEach((debt) => {
      if (debt?.onCheck?.select && debt.onCheck.select.length > 0) {
        const mandatoryDebts = debt.onCheck.select;

        mandatoryDebts.forEach((mandatory) => {
          if (!allSelectedCodes.includes(mandatory.debtsCode)) {
            allSelectedCodes.push(mandatory.debtsCode);
            selectedDebtsAndMandatoryByType.push(mandatory);
          }
        });
      }
      const debtLikeMandatory = {
        additionalDescription: debt.additionalDescription ?? '',
        consultNumber: debt.consultNumber,
        debtsCode: debt.debtsCode,
        description: debt.description,
        price: debt.price,
        type: debt.type,
      };

      if (!allSelectedCodes.includes(debt.debtsCode)) {
        allSelectedCodes.push(debt.debtsCode);
        selectedDebtsAndMandatoryByType.push(debtLikeMandatory);
      }
    });

    const selectedMultas = selectedDebtsAndMandatoryByType.filter(
      (debt) => debt?.type === 'multas',
    );
    const selectedIPVA = selectedDebtsAndMandatoryByType.filter(
      (debt) => debt?.type === 'ipva',
    );
    const selectedLicenciamentos = selectedDebtsAndMandatoryByType.filter(
      (debt) => debt?.type === 'licenciamento',
    );
    const selectedOutros = selectedDebtsAndMandatoryByType.filter(
      (debt) => debt?.type === 'outros',
    );

    setTotalIPVA(sumSelectedDebtsWithMandatoryDebts(selectedIPVA) ?? 0);
    setTotalMultas(sumSelectedDebtsWithMandatoryDebts(selectedMultas) ?? 0);
    setTotalLicenciamento(
      sumSelectedDebtsWithMandatoryDebts(selectedLicenciamentos) ?? 0,
    );
    setTotalOutros(sumSelectedDebtsWithMandatoryDebts(selectedOutros) ?? 0);
  }, [isChecked, dataList]);

  const handleChangePlateValue = (text: string) => {
    setPlateToShow(text);

    let debounceTimer;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      setPlate(handleValue(text.toUpperCase()));
    }, 500);
  };

  const handleChangeExportFileName = (event: any) => {
    const upperCaseValue = event.target.value;
    setExportFilename(upperCaseValue);
  };

  useEffect(() => {
    refetch();
    setTableColumnsConfig(handleColumnsToInitialState());
  }, []);

  useEffect(() => {
    const newHeaders = tableColumnsConfig
      .filter((col) => col.show)
      .map((col) => headersInitialState.find((head) => head?.id == col.id));

    if (newHeaders && newHeaders.length > 0) {
      setHeaders(newHeaders);
    }
  }, [tableColumnsConfig]);

  useEffect(() => {
    const refetchNewVehicle =
      (location?.state && location?.state?.newVehicle) || null;
    if (refetchNewVehicle) {
      refetch();
    }
  }, [location.state, refetch]);

  function sumSelectedDebtsWithMandatoryDebts(debts: IMandatoryDebt[]) {
    return debts.reduce((total, debt) => total + Number(debt?.price ?? 0), 0);
  }

  const handleSetAllSelected = () => {
    if (!isAllChecked && dataList?.results && dataList?.results?.length > 0) {
      const newIsChecked = dataList.results.reduce(
        (acc, each) => ({ ...acc, [each.debtsCode]: true }),
        {},
      );
      setIsChecked(newIsChecked);
      setIsAllChecked(true);
    }
    if (isAllChecked && dataList?.results && dataList?.results?.length > 0) {
      const newIsChecked = dataList.results.reduce(
        (acc, each) => ({ ...acc, [each.debtsCode]: false }),
        {},
      );
      setIsChecked(newIsChecked);
      setIsAllChecked(false);
    }
  };

  function handleColumnsToInitialState() {
    const persistedConfig: IItem[] = JSON.parse(
      localStorage.getItem('@Zignet:tabela-debitos')!,
    );

    if (persistedConfig && persistedConfig.length > 0) return persistedConfig;

    return headersInitialState?.map((col, index) => ({
      id: `item-${index}`,
      content: col?.label,
      show: true,
    }));
  }

  function validateRowsChecked(obj: { [key: string]: boolean }): boolean {
    return Object.values(obj).some((value) => value === true);
  }
  const handlePayButton = async () => {
    const keepDebts = true;
    handleConsultClean(keepDebts);
    if (selectedDebts.length > 0) {
      navigate('/debts/batch-payment');
    }
  };

  async function handleExportFile(name: string) {
    if (!name || name.length < 3) {
      return Swal.fire({
        icon: 'error',
        title: 'Nome inválido!',
        html: `O nome do arquivo deve ter ao menos 3 caracteres`,
        confirmButtonColor: theme.colors.orange.primary,
      });
    }

    const data = {
      name,
      type: 'Débitos',
      pageOptions: {
        page,
        resultsPerPage: itemsPerPage,
        orderByDirection: orderByDirection.toUpperCase(),
      },
      duplicated: false,
      customFilters: {
        plate,
        driverName: driver,
        dueDate: {
          end: endDate?.toString(),
          start: initialDate?.toString(),
        },
        finalNumberPlate: endPlate,
      },
      filters: {
        vehiclePlate: endPlate,
        vehicleUF: uf?.value,
        type: debtType?.value,
        installments: parcelaIpva,
        isUniqueInstallments,
        isExpirationDate: debtStatus?.value
          ? debtStatus.value !== 'EXPIRING'
          : undefined,
      },
    };

    createExportedFile(data)
      .then((res) => {
        setOpenExportModal(false);
        setOpenGeneratingExportModal(true);
      })
      .catch((err) => {
        setOpenExportModal(false);
        return Swal.fire({
          icon: 'error',
          title: 'Falha ao exportar débitos!',
          html: `Ocorreu uma falha inesperada ao exportar os débitos`,
          confirmButtonColor: theme.colors.orange.primary,
        });
      });
  }

  function handleMandatoryDebtsByType(mandatoryDebts: IMandatoryDebt[]) {
    const allTypes: string[] = [];
    mandatoryDebts.forEach((debt) => {
      if (!allTypes.includes(debt.type)) {
        allTypes.push(debt.type);
      }
    });

    return allTypes.map((type) => ({
      type,
      data: mandatoryDebts.filter((debt) => debt.type === type),
    }));
  }

  async function handleApplyAdvancedFilters(props: IHandleApplyAdvancedFiltersProps) {
    setPlateToShow(props?.plate)

    setPlate(handleValue(props?.plate?.toUpperCase()));
    setDriver(handleValue(props?.conductor));
    setDebtType(props.debtType);
    setDebtStatus(props.status);

    if (handleValue(props.endDate?.toString())) {
      setEndDate(formatDateToISO(props.endDate ?? ''));
    } else {
      setEndDate(undefined);
    }

    if (handleValue(props.endDate?.toString())) {
      setInitialDate(formatDateToISO(props.initialDate ?? ''));
    } else {
      setInitialDate(undefined);
    }

    setEndPlate(handleValue(props.endPlate))
    setParcelaIpva(handleValue(props.parcelaIpva))
    setIsUniqueInstallments(props.isUniqueInstallments)

    if (handleValue(props.uf)) {
      setUf({
        value: getEnumKeyByValue(UF, props.uf ?? "") ?? "",
        label: props.uf ?? ""
      })

    } else {

      setUf(undefined)
    }
  }

  return (
    <VehicleContainer>
      {isLoadingDebts && <Loading />}

      {loadingConsult && (
        <LoadingConsult
          fast={false}
          setLoading={setIsLoading}
          texts={[
            'Requisição iniciada',
            'Estamos buscando sua(s) placa(s). Por favor, aguarde',
            'Acessando os dados do(s) veículo(s) no Detran',
            'Processamento de débitos em andamento',
            'Sua consulta está sendo finalizada',
          ]}
          time={120}
        />
      )}

      <GenericModal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        iconTitle={
          <AiFillQuestionCircle style={{ fontSize: 48, color: '#F58220' }} />
        }
        title="Deseja realmente atualizar a frota?"
        showActionsButtons
        leftButtonText={'Voltar'}
        leftButtonAction={() => setConsultsPackageModal(undefined)}
        rightButtonText={'Salvar'}
        rightButtonAction={() => {
          arrayActionsConfirmModal.map((action) => action());
          setShowConfirmModal(false);
        }}
      >
        <p style={{ textAlign: 'center' }}>
          Quantidade de veículos cadastrados: {vehiclesTotal}
        </p>
      </GenericModal>

      <GenericModal
        open={showIsLoadingFleet}
        setOpen={setShowIsLoadingFleet}
        iconTitle={<HiClock style={{ fontSize: 48, color: '#F58220' }} />}
        title="Atualização iniciada"
        showActionsButtons
        leftButtonStyle={{ display: 'none' }}
        rightButtonText={'Fechar'}
        onClose={() => setConsultsPackageModal(undefined)}
        rightButtonAction={() => setShowIsLoadingFleet(false)}
        rightButtonStyle={{ maxWidth: '140px' }}
      >
        <p style={{ maxWidth: '430px' }}>
          A atualização de sua frota foi iniciada. Você será notificado assim
          que o processo for finalizado.
        </p>
      </GenericModal>

      <GenericModal
        open={showFinishLoadingFleet}
        setOpen={setShowFinishLoadingFleet}
        iconTitle={<FaCircleCheck style={{ fontSize: 48, color: '#F58220' }} />}
        title="Atualização de frota concluída"
        showActionsButtons
        leftButtonStyle={{ display: 'none' }}
        rightButtonText={'Fechar'}
        onClose={() => setConsultsPackageModal(undefined)}
        rightButtonAction={() => {
          setConsultsPackageModal(undefined);
          setShowFinishLoadingFleet(false);
        }}
        rightButtonStyle={{ maxWidth: '140px' }}
      >
        <p style={{ textAlign: 'center' }}>
          A frota foi atualizada com sucesso
        </p>
      </GenericModal>

      <ConfigureTableModal
        show={openConfigureTableModal}
        setShow={setOpenConfigureTableModal}
        initialItems={handleColumnsToInitialState()}
        setTableColumns={setTableColumnsConfig}
      />

      <AdvancedFiltersModal
        open={openGlobalFiltersModal}
        setOpen={setOpenGlobalFiltersModal}
        plateTable={plate}
        ufTable={uf}
        debtTypeTable={debtType}
        statusTable={debtStatus}
        handleApplyFilters={handleApplyAdvancedFilters}
      />

      <GenericModal
        open={openExportModal}
        setOpen={setOpenExportModal}
        showActionsButtons
        rightButtonText="Exportar"
        leftButtonText="Voltar"
        title="Exportar relatório"
        text="Defina um nome para o relatório que deseja exportar"
        iconTitle={
          <PiArrowLineUpRight style={{ fontSize: 48, color: '#F58220' }} />
        }
        rightButtonAction={async () => {
          await handleExportFile(exportFilename);
          setExportFilename('');
        }}
      >
        <ExportContainer>
          <StyledInput
            placeholder="Digite o nome do relatório"
            value={exportFilename}
            onChange={handleChangeExportFileName}
            maxLength={50}
          />
        </ExportContainer>
      </GenericModal>

      <GenericModal
        open={openGeneratingExportModal}
        setOpen={setOpenGeneratingExportModal}
        showActionsButtons
        rightButtonText="Ir para exportações"
        leftButtonText="Voltar"
        title="Gerando relatório..."
        text="O relatório está sendo gerado, você será informado por e-mail quando o processo for finalizado."
        iconTitle={<HiClock style={{ fontSize: 48, color: '#F58220' }} />}
        rightButtonAction={() => navigate('/exports')}
        containerStyle={{ width: '450px', textAlign: 'center' }}
        actionsStyle={{ width: '400px' }}
      />

      <GenericModal
        open={openMandatoryDebtsModal}
        setOpen={setOpenMandatoryDebtsModal}
        showActionsButtons
        leftButtonStyle={{ display: 'none' }}
        rightButtonText={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
            }}
          >
            <span
              style={{
                fontSize: '14px',
                lineHeight: '21px',
                fontWeight: '500',
              }}
            >
              Fechar
            </span>
            <IoIosArrowDroprightCircle style={{ fontSize: '15px' }} />
          </div>
        }
        rightButtonAction={() => setOpenMandatoryDebtsModal(false)}
        rightButtonStyle={{ width: 'fit-content', padding: '14px 32px' }}
      >
        <MandatoryDebtsContainer>
          <span className="title-children">Débitos Obrigatórios</span>
          {handleMandatoryDebtsByType(mandatoryDebts).map((container) => (
            <div className="mandatory-debt">
              <span className="debt-type">{container.type?.toUpperCase()}</span>

              {container.data.map((debt) => (
                <div className="debt-desc">
                  <span>{debt.additionalDescription && debt.additionalDescription.length > 0 ? debt.additionalDescription : debt.description ?? container.type}</span>
                  <span>{`R$ ${(debt.price ?? 0)?.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}</span>
                </div>
              ))}
            </div>
          ))}
        </MandatoryDebtsContainer>
      </GenericModal>

      <AdminTitle
        title="Débitos Veículares"
        icon={<PiMoney style={{ fontSize: '30px' }} />}
      />

      <InfoCardsWrapper>
        {totalDebtsData.map((data, index) => (
          <InfoCard
            isLoading={isLoadingDebts}
            key={index + data.titleCard}
            titleCard={data.titleCard}
            totalValue={data.totalValue}
          />
        ))}
      </InfoCardsWrapper>

      <FiltersContainer>
        <div className="update-fleet">
          <StyledButton
            text="Atualizar Frota"
            textColor="white"
            backgroundColor="#0C9AD7"
            icon={<PiArrowClockwise style={{ fontSize: 26 }} />}
            iconPosition="left"
            onClick={handlePostConsultsPackageByUserSub}
            disabled={!!consultsPackageStatus}
          />
          {consultsPackageStatus && (
            <BatchProgressStatusBar
              completedPercentage={batchDebtsConsultsProgress}
              completed={consultsCompletedStatus ?? 0}            
              total={consultsPackageStatus.consultas.length}
            />
            // <ConsultsPackageInProgress>
            //   <ProgressBar
            //     completed={batchDebtsConsultsProgress}
            //     bgColor={theme.colors.blue.light}
            //     isLabelVisible={false}
            //   />

            //   <div>
            //     <span>
            //       {consultsCompletedStatus ?? 0} de {consultsPackageStatus.consultas.length ?? 0} consultado(s)
            //     </span>

            //     {successfulAndFailedDebtConsults?.failed > 0 && (
            //       <span>
            //         {successfulAndFailedDebtConsults.failed} falharam
            //       </span>
            //     )}
            //   </div>
            // </ConsultsPackageInProgress>
          )}
        </div>
        <StyledButton
          text="Exportar"
          textColor="white"
          backgroundColor="#005EAD"
          icon={<PiArrowLineUpRight style={{ fontSize: 26 }} />}
          iconPosition="left"
          onClick={() => setOpenExportModal(true)}
        />
      </FiltersContainer>

      <FiltersContainer>
        <label
          htmlFor=""
          className="inputSearch"
        // style={{ gridColumn: 'span 2' }}
        >
          <StyledInput
            placeholder="Placa"
            value={plateToShow}
            maxLength={7}
            onChange={(e) => handleChangePlateValue(e.target.value)}
          />
          <span className="debt-filter">
            <FiSearch />
          </span>
        </label>
        <ReactSelect
          placeholder="Selecione a UF"
          isLoading={isLoadingDebts}
          value={uf}
          name={"uf"}
          setSelectedOption={setUf}
          allOptions={Object.keys(UF).map((key) => ({
            label: UF[key as keyof typeof UF],
            value: getEnumKeyByValue(UF, UF[key as keyof typeof UF]) ?? "",
          }))}
          isClearable
        />

        <ReactSelect
          placeholder="Status"
          isLoading={isLoadingDebts}
          allOptions={statusOptions}
          setSelectedOption={setDebtStatus}
          value={debtStatus}
          name="status"
          isClearable
        />
        <ReactSelect
          placeholder="Tipo de Débito"
          isLoading={isLoadingDebts}
          allOptions={debtTypeOptions}
          setSelectedOption={setDebtType}
          value={debtType}
          name="UF"
          isClearable
        />
        <StyledButton
          text="Filtros Avançados"
          textColor="white"
          backgroundColor="#FAB040"
          icon={<IoFilter style={{ fontSize: 26 }} />}
          iconPosition="left"
          onClick={() => setOpenGlobalFiltersModal(true)}
        />
        <StyledButton
          text="Configurar Tabela"
          textColor="#F58220"
          backgroundColor="white"
          border="1px solid #F58220"
          icon={
            <LuAlignEndHorizontal style={{ fontSize: 26, color: '#F58220' }} />
          }
          iconPosition="left"
          onClick={() => setOpenConfigureTableModal(true)}
        />
        <StyledButton
          text="Pagar"
          textColor={theme.colors.white}
          backgroundColor={theme.colors.orange.primary}
          icon={<TbCreditCard style={{ fontSize: 26 }} />}
          iconPosition="left"
          onClick={handlePayButton}
          disabled={!validateRowsChecked(isChecked)}
        />
      </FiltersContainer>

      <DebtsDataTable
        title="Listagem de Débitos"
        heads={headers as HeadCell[]}
        hasPagination
        data={dataList?.results}
        order={orderByDirection}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        page={Number(dataList?.currentPage)}
        setPage={setPage}
        itemsPerPage={itemsPerPage}
        nPages={dataList?.nPages}
        isLoading={isLoadingDebts}
        error={error}
        totalItems={dataList?.total}
        hasCheckbox={true}
        isChecked={isAllChecked}
        setIsChecked={handleSetAllSelected}
      >
        <TableBody>
          <ConfiguredTableRow
            data={dataList}
            configuredColumns={tableColumnsConfig}
            setIsChecked={setIsChecked}
            isChecked={isChecked}
            handleRowAction={setOpenMandatoryDebtsModal}
            setMandatoryDebts={setMandatoryDebts}
          />
        </TableBody>
      </DebtsDataTable>
    </VehicleContainer>
  );
};

export default DebtsListing;
